import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Text } from '../../../../../../../CommonComponents/OPL/Text/Text';
import { Select } from '../../../../../../../CommonComponents/OPL/Select/Select/Select';
import { InputNumberMask } from '../../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import {
  Donation,
  DonationSource,
  DonationSourceType,
  DonorType,
  FormValues,
  Options,
} from './EstatePlanningCharitableDonations.meta';
import {
  mapDonationSourceToOption,
  useEstatePlanningCharitableDonations,
} from './useEstatePlanningCharitableDonations';
import { Button } from '../../../../../../../CommonComponents/OPL/Button/Button';
import { Box } from '../../../../../../../CommonComponents/OPL/Box/Box';
import { IconButton } from '../../../../../../../CommonComponents/OPL/IconButton/IconButton';
import { formatCurrency } from '../../../../../../../CommonUtils/formatNumber';

const NEW_DONATION: Donation = {
  Donor: DonorType.Principal,
  DonationAmount: null,
  RecipientId: null,
  RecipientName: null,
  SourceId: null,
  IsApplied: false,
  SourceType: null,
  SourceName: '',
  EntityId: null,
  IsSimulatedSource: false,
  SimulatedAccountType: null,
};

interface Props {
  options: Options;
}

export const DonationFieldArray = ({ options }: Props) => {
  const formBag = useFormContext<FormValues>();
  const donationsBag = useEstatePlanningCharitableDonations({ options });

  const fieldArray = donationsBag.donationsFieldArray;

  const fieldName = 'Donations';

  const errors = formBag.formState.errors.Donations;

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
      {fieldArray.fields.map((donation, index) => {
        const isSpouse = donation.Donor === DonorType.Spouse;
        const isApplied = donation.IsApplied;

        const sourceOptions = isSpouse
          ? donationsBag.spouseSourceOptions
          : donationsBag.primarySourceOptions;

        const selectedSource = isApplied
          ? // Display the Source Name in the dropdown for applied donations since the source
            // might no longer be present in the sourceOptions
            {
              ...mapDonationSourceToOption((donation as unknown) as DonationSource),
              value: donation.SourceId,
              label: donation.SourceName,
            }
          : sourceOptions
              .flatMap(obj => obj.options)
              .find(({ value: v }) => v === donation.SourceId);

        return (
          <React.Fragment key={donation.key}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Text fontWeight={500}>{`Charitable Donation ${index + 1}`}</Text>

              {!isApplied && (
                <IconButton
                  variant="subtleInactive"
                  icon="far fa-minus-circle"
                  onClick={() => fieldArray.remove(index)}
                />
              )}
            </Box>

            <Controller
              control={formBag.control}
              name={`${fieldName}.${index}.Donor`}
              render={({ field: { ref, value, onChange } }) => {
                return (
                  <Select
                    innerRef={ref}
                    label="Donor"
                    value={donationsBag.donorOptions.find(({ value: v }) => v === value)}
                    options={donationsBag.donorOptions}
                    onChange={opt => {
                      if (opt) {
                        fieldArray.update(index, NEW_DONATION);

                        onChange(opt.value);
                      }
                    }}
                    formVariant={errors?.[index]?.Donor ? 'error' : undefined}
                    helperText={errors?.[index]?.Donor?.message}
                    disabled={isApplied}
                  />
                );
              }}
            />

            <Controller
              control={formBag.control}
              name={`${fieldName}.${index}.SourceId`}
              render={({ field: { ref, onChange } }) => (
                <Select
                  innerRef={ref}
                  label="From"
                  value={selectedSource}
                  options={sourceOptions}
                  onChange={opt => {
                    if (opt) {
                      onChange(opt.value);

                      formBag.setValue(`${fieldName}.${index}.SourceName`, opt.label);
                      formBag.setValue(
                        `${fieldName}.${index}.DonationAmount`,
                        opt.type === DonationSourceType.OtherAssets ? opt.amount : null,
                      );
                      formBag.setValue(`${fieldName}.${index}.SourceType`, opt.type);
                      formBag.setValue(
                        `${fieldName}.${index}.IsSimulatedSource`,
                        opt.isSimulatedSource,
                      );
                      formBag.setValue(
                        `${fieldName}.${index}.SimulatedAccountType`,
                        opt.simulatedAccountType,
                      );
                      formBag.setValue(`${fieldName}.${index}.EntityId`, opt.entityId);
                    }
                  }}
                  placeholder="Select Account"
                  formVariant={errors?.[index]?.SourceId ? 'error' : undefined}
                  helperText={errors?.[index]?.SourceId?.message}
                  disabled={isApplied}
                />
              )}
            />

            <Controller
              control={formBag.control}
              name={`${fieldName}.${index}.RecipientName`}
              render={({ field: { ref } }) => (
                <Select
                  innerRef={ref}
                  isCreatable
                  label="To"
                  value={
                    donation.RecipientName
                      ? {
                          label: donation.RecipientName,
                          value: donation.RecipientId,
                        }
                      : null
                  }
                  onChange={opt => {
                    if (opt) {
                      formBag.setValue(`${fieldName}.${index}.RecipientName`, opt.label, {
                        shouldValidate: true,
                      });
                      formBag.setValue(`${fieldName}.${index}.RecipientId`, opt.value);
                    }
                  }}
                  onCreateOption={recipientName => {
                    formBag.setValue(`${fieldName}.${index}.RecipientName`, recipientName, {
                      shouldValidate: true,
                    });
                    formBag.setValue(`${fieldName}.${index}.RecipientId`, null);
                  }}
                  options={donationsBag.recipientOptions}
                  formVariant={errors?.[index]?.RecipientName ? 'error' : undefined}
                  helperText={errors?.[index]?.RecipientName?.message}
                  noOptionsMessage={() => 'No available options. Please type a Charity name.'}
                  disabled={isApplied}
                />
              )}
            />

            <Controller
              control={formBag.control}
              name={`${fieldName}.${index}.DonationAmount`}
              render={({ field: { value, onChange } }) => (
                <InputNumberMask
                  label="Amount"
                  leftIcon="far fa-dollar-sign"
                  name="amount"
                  value={value ?? ''}
                  decimalScale={2}
                  disabled={donation.SourceType === DonationSourceType.OtherAssets || isApplied}
                  onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                  formVariant={errors?.[index]?.DonationAmount ? 'error' : undefined}
                  helperText={
                    errors?.[index]?.DonationAmount?.message ||
                    (!donation.IsApplied &&
                    selectedSource &&
                    donation.SourceType !== DonationSourceType.OtherAssets
                      ? `Available amount: ${formatCurrency(selectedSource.amount, {
                          mantissa: 2,
                        })}`
                      : '')
                  }
                  tooltip={
                    donation.SourceType === DonationSourceType.OtherAssets
                      ? 'The amount displayed here will be deducted from the adjusted gross estate and help determine the value of the taxable estate.'
                      : 'Enter intended deductible charitable gifts and donations. The amount entered will be deducted from the adjusted gross estate and help determine the value of the taxable estate.'
                  }
                />
              )}
            />
          </React.Fragment>
        );
      })}

      {fieldArray.fields.every(donation => donation.IsApplied) && (
        <Button
          variant="link"
          leftIcon="fas fa-plus"
          onClick={() => fieldArray.append(NEW_DONATION)}
          style={{ alignSelf: 'flex-start' }}
        >
          {`Add ${fieldArray.fields.length > 0 ? 'Additional ' : ''}Donation`}
        </Button>
      )}
    </Box>
  );
};
